// services/courseService.js - Updated for modern dashboard integration
import { get, post, put, del } from '@aws-amplify/api-rest';
import { fetchAuthSession } from 'aws-amplify/auth';
import * as emailService from './emailService';

const API_NAME = 'CourseBookingApi';
console.log("✅ API_NAME IS NOW:", API_NAME);

// Helper to get auth headers
async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    if (!session || !session.tokens || !session.tokens.idToken) {
      console.error('Authentication Failure: No valid session or token found.');
      throw new Error('No authentication token');
    }
    const headers = {
      Authorization: `Bearer ${session.tokens.idToken.toString()}`
    };
    //console.log('Authorization Headers:', headers);
    return headers;
  } catch (error) {
    console.error('Error fetching auth session:', error);
    throw error;
  }
}

// Enhanced error handling wrapper (unused - kept for reference)
// const handleApiCallOrig = async (apiCall) => {
//   try {
//     let response = await apiCall();
//     if (response && response.response && typeof response.response.then === 'function') {
//       response = await response.response;
//     }
//     if (response && typeof response.json === 'function') {
//       response = await response.json();
//     }
//     if (response && response.body && typeof response.body.getReader === 'function') {
//       const reader = response.body.getReader();
//       let chunks = [];
//       let done, value;
//       while (!done) {
//         ({ done, value } = await reader.read());
//         if (value) chunks.push(...value);
//       }
//       const text = new TextDecoder().decode(new Uint8Array(chunks));
//       response = JSON.parse(text);
//     }
//     return response;
//   } catch (error) {
//     console.error(`API Error in ${apiCall.name}:`, error);
//     if (error.statusCode === 401 || error.name === 'NotAuthorizedException') {
//       console.error('Authentication error - user may need to sign in again');
//     }
//     if (process.env.NODE_ENV === 'development') {
//       console.warn(`Returning mock data for ${apiCall.name}`);
//       return getMockData(apiCall.name);
//     }
//     throw error;
//   }
// };

// function parseApiResponse(response) {
//   if (response && typeof response.body === "string") {
//     try {
//       const body = JSON.parse(response.body);
//       if (Array.isArray(body.forms)) return body.forms;
//       if (Array.isArray(body)) return body;
//       return [body];
//     } catch (e) {
//       // Not JSON
//     }
//   }
//   if (response && Array.isArray(response.forms)) return response.forms;
//   if (Array.isArray(response)) return response;
//   return [];
// }

const handleApiCall = async (apiCall) => {
  try {
    let response = await apiCall();

    // UNWRAP {response: Promise, ...}
    if (response && response.response && typeof response.response.then === 'function') {
      response = await response.response;
    }

    // Parse fetch Response
    if (response && typeof response.json === 'function') {
      response = await response.json();
    }

    // Handle ReadableStream (Amplify/fetch)
    if (response && response.body && typeof response.body.getReader === 'function') {
      try {
        const reader = response.body.getReader();
        let chunks = [];
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (value) chunks.push(value); // Don't spread - causes stack overflow on large responses
        }
        // Concatenate all chunks into single Uint8Array
        const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
        const combined = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
          combined.set(chunk, offset);
          offset += chunk.length;
        }
        const text = new TextDecoder().decode(combined);
        const parsed = JSON.parse(text);
        response = { ...response, ...parsed };
        delete response.body; // Remove the consumed stream
      } catch (e) {
        console.error('Failed to read response body stream:', e);
      }
    }

    // If .body is a string, parse it and merge (the classic Lambda proxy pattern)
    if (response && typeof response.body === 'string') {
      try {
        const parsed = JSON.parse(response.body);
        response = { ...response, ...parsed };
        delete response.body;
      } catch (e) {
        // Not JSON, ignore
      }
    }

    return response;
  } catch (error) {
    throw error;
  }
};








// Mock data for development
const getMockData = (apiName) => {
  const mockData = {
    fetchAdminCourses: [
      {
        courseId: 'mock-course-1',
        title: 'React Fundamentals',
        description: 'Learn the basics of React',
        status: 'published',
        totalSpots: 20,
        availableSpots: 15,
        startDate: '2024-03-01'
      },
      {
        courseId: 'mock-course-2',
        title: 'Advanced JavaScript',
        description: 'Master modern JavaScript techniques',
        status: 'draft',
        totalSpots: 25,
        availableSpots: 25,
        startDate: '2024-03-15'
      }
    ],
    getCourseById: {
      courseId: 'mock-course-1',
      title: 'React Fundamentals',
      description: 'Learn the basics of React',
      status: 'published',
      totalSpots: 20,
      availableSpots: 15,
      startDate: '2024-03-01',
      form: {
        formId: 'mock-form-1',
        title: 'React Fundamentals Registration',
        fields: [
          {
            id: 'field-1',
            type: 'text',
            label: 'Full Name',
            required: true,
            name: 'full_name'
          },
          {
            id: 'field-2',
            type: 'email',
            label: 'Email Address',
            required: true,
            name: 'email'
          }
        ]
      }
    },
    getStudentBookings: [
      {
        id: 'mock-booking-1',
        courseId: 'mock-course-1',
        courseName: 'React Fundamentals',
        status: 'Confirmed'
      }
    ],
    getMyWaitlist: [
      {
        id: 'mock-waitlist-1',
        courseId: 'mock-course-3',
        courseName: 'Full Stack Development',
        position: 2
      }
    ],
    getStudentPayments: [
      {
        id: 'mock-payment-1',
        bookingId: 'mock-booking-1',
        amount: 299.00,
        status: 'Pending'
      }
    ],
    // In the getMockData function, add:
    getAllBookings: [
      {
        bookingId: 'booking-001',
        studentName: 'John Doe',
        studentEmail: 'john.doe@example.com',
        studentId: 'student-001',
        courseId: 'mock-course-1',
        courseName: 'React Fundamentals',
        status: 'confirmed',
        paymentStatus: 'paid',
        paymentType: 'Full Payment',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        bookingId: 'booking-002',
        studentName: 'Jane Smith',
        studentEmail: 'jane.smith@example.com',
        studentId: 'student-002',
        courseId: 'mock-course-1',
        courseName: 'React Fundamentals',
        status: 'pending',
        paymentStatus: 'pending',
        paymentType: 'Installment Plan',
        createdAt: new Date(Date.now() - 86400000).toISOString(),
        updatedAt: new Date(Date.now() - 86400000).toISOString()
      },
      {
        bookingId: 'booking-003',
        studentName: 'Bob Johnson',
        studentEmail: 'bob.j@example.com',
        studentId: 'student-003',
        courseId: 'mock-course-2',
        courseName: 'Advanced JavaScript',
        status: 'waitlisted',
        paymentStatus: 'pending',
        paymentType: null,
        createdAt: new Date(Date.now() - 172800000).toISOString(),
        updatedAt: new Date(Date.now() - 172800000).toISOString()
      }
    ],
    getCourseBookings: [
      // Same as above but filtered by course
      {
        bookingId: 'booking-001',
        studentName: 'John Doe',
        studentEmail: 'john.doe@example.com',
        studentId: 'student-001',
        courseId: 'mock-course-1',
        courseName: 'React Fundamentals',
        status: 'confirmed',
        paymentStatus: 'paid',
        paymentType: 'Full Payment',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    ]
  };

  return mockData[apiName] || [];
};

// ===============================
// COURSE MANAGEMENT FUNCTIONS
// ===============================

/**
 * Create a new course with form fields (for modern dashboard)
 */
export const createCourseOrig = (courseData) => handleApiCall(async function createCourse() {
  console.log('Creating course with data:', courseData);

  // Transform the data to match backend expectations
  const payload = {
    title: courseData.title,
    description: courseData.description,
    startDate: courseData.startDate,
    endDate: courseData.endDate,
    totalSpots: courseData.totalSpots || courseData.capacity,
    status: courseData.status || 'draft',
    allowedPaymentTypes: courseData.allowedPaymentTypes || ['card', 'bank-transfer'],
    restrictedToEmails: courseData.restrictedToEmails,
    // Include form fields if provided
    formFields: courseData.formSchema || courseData.formFields || [],
    formId: courseData.formId // Include formId if provided
  };

  return await post({
    apiName: API_NAME,
    path: '/admin/courses',
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Create a new course with payment types and email restrictions
 */
export const createCourse = (courseData) => handleApiCall(async function createCourse() {
  console.log('Creating course with data:', courseData);

  // Transform the data to match backend expectations
  const payload = {
    title: courseData.title,
    description: courseData.description,
    startDate: courseData.startDate,
    endDate: courseData.endDate,
    balanceDueDate: courseData.balanceDueDate || null, // When balance payment is due
    formEditLockDate: courseData.formEditLockDate || null, // After this date students cannot edit booking forms (admin override; default = 6 weeks before start)
    totalSpots: courseData.totalSpots || courseData.capacity,
    status: courseData.status || 'draft',

    // RENAMED: allowedPaymentTypes -> paymentMethods (how students pay)
    paymentMethods: courseData.paymentMethods || ['card', 'bank-transfer', 'paypal', 'invoice'],

    // Email restrictions (global course level)
    restrictedToEmails: courseData.restrictedToEmails,

    // Pricing
    // €0 is a valid base price (free courses billed directly to a college) —
    // only fall back to the default when the value is genuinely absent
    basePrice: courseData.basePrice !== null && courseData.basePrice !== undefined && courseData.basePrice !== ''
      ? parseFloat(courseData.basePrice) : 299.00,
    currency: courseData.currency || 'EUR',

    // Visibility options - preserve explicit false values
    hideFromGuardian: courseData.hideFromGuardian !== undefined ? courseData.hideFromGuardian : false,
    hideFromStudent: courseData.hideFromStudent !== undefined ? courseData.hideFromStudent : false,

    // Form association
    formId: courseData.formId,
    formFields: courseData.formSchema || courseData.formFields || [],

    // PAYMENT TYPES: Transform frontend paymentTypes to backend format
    paymentTypes: courseData.paymentTypes ? courseData.paymentTypes.map(pt => ({
      paymentTypeId: pt.paymentTypeId,
      isDefault: pt.isDefault || false,
      isAvailable: pt.isAvailable !== false,
      customAmount: pt.customAmount !== null && pt.customAmount !== undefined && pt.customAmount !== ''
        ? parseFloat(pt.customAmount) : null,

      // NEW: Email restrictions for this specific payment type
      restrictedToEmails: pt.restrictedToEmails || null,

      // NEW: Additional payment type metadata
      requiresApproval: pt.requiresApproval || false,
      validFrom: pt.validFrom || null,
      validUntil: pt.validUntil || null,
      maxUses: pt.maxUses || null,

      // Discount/pricing overrides
      discountPercentage: pt.discountPercentage || null,
      discountAmount: pt.discountAmount || null,

      // Notes/description for this assignment
      notes: pt.notes || null
    })) : [],

    // NEW: Email-specific restrictions (separate from payment types)
    emailRestrictions: courseData.emailRestrictions ? courseData.emailRestrictions.map(restriction => ({
      email: restriction.email.toLowerCase(),
      allowedPaymentTypes: restriction.allowedPaymentTypes || [],
      customPricing: restriction.customPricing || null,
      specialDiscount: restriction.specialDiscount || null,
      notes: restriction.notes || null,
      isActive: restriction.isActive !== false
    })) : [],

    // Find default payment type ID
    defaultPaymentTypeId: courseData.paymentTypes ?
      courseData.paymentTypes.find(pt => pt.isDefault)?.paymentTypeId : null,

    // Publishing settings
    publishDate: courseData.publishDate,
    visibleFrom: courseData.visibleFrom,
    
    // Data management fields
    coursePrefix: courseData.coursePrefix || null,
    courseTypeId: courseData.courseTypeId || courseData.courseType || null,
    centreId: courseData.centreId || courseData.centre || null,
    centreName: courseData.centreName || null,
    targetYearLevels: courseData.targetYearLevels || [],
    studentEligibility: courseData.studentEligibility || null,
    
    // Age restrictions
    minAge: courseData.minAge || null,
    maxAge: courseData.maxAge || null,

    // Gender-based slots
    genderPolicy: courseData.genderPolicy || 'mixed',
    maleSlots: courseData.maleSlots || courseData.slotsMale || 0,
    femaleSlots: courseData.femaleSlots || courseData.slotsFemale || 0,
    mixedSlots: courseData.mixedSlots || 0,
    virtualSlots: courseData.virtualSlots || 0
  };

  console.log('=== FRONTEND PAYLOAD DEBUG ===');
  console.log('Original courseData.paymentMethods:', courseData.paymentMethods);
  console.log('Original courseData.paymentTypes:', courseData.paymentTypes);
  console.log('Transformed payload.paymentMethods:', payload.paymentMethods);
  console.log('Transformed payload.paymentTypes:', payload.paymentTypes);
  console.log('Complete coursePayload:', JSON.stringify(payload, null, 2));

  return await post({
    apiName: API_NAME,
    path: '/admin/courses',
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Update an existing course (for modern dashboard)
 */
export const updateCourseOrig = (courseId, courseData) => handleApiCall(async function updateCourse() {
  console.log('Updating course:', courseId, 'with data:', courseData);

  const payload = {
    title: courseData.title,
    description: courseData.description,
    startDate: courseData.startDate,
    endDate: courseData.endDate,
    totalSpots: courseData.totalSpots || courseData.capacity,
    status: courseData.status,
    allowedPaymentTypes: courseData.allowedPaymentTypes,
    restrictedToEmails: courseData.restrictedToEmails,
    // Include form fields if provided
    formFields: courseData.formSchema || courseData.formFields,
    formId: courseData.formId // Include formId if updating an existing form

  };

  return await put({
    apiName: API_NAME,
    path: `admin/courses/${courseId}`,
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Update an existing course with payment types and email restrictions
 */
export const updateCourse = (courseId, courseData) => handleApiCall(async function updateCourse() {
  console.log('Updating course:', courseId, 'with data:', courseData);

  // Transform the data to match backend expectations (same as createCourse)
  const payload = {
    title: courseData.title,
    description: courseData.description,
    startDate: courseData.startDate,
    endDate: courseData.endDate,
    balanceDueDate: courseData.balanceDueDate || null, // When balance payment is due
    formEditLockDate: courseData.formEditLockDate || null, // After this date students cannot edit booking forms (admin override; default = 6 weeks before start)
    totalSpots: courseData.totalSpots || courseData.capacity,
    status: courseData.status,

    // RENAMED: allowedPaymentTypes -> paymentMethods (how students pay)
    paymentMethods: courseData.paymentMethods || ['card', 'bank-transfer'],

    // Email restrictions (global course level)
    restrictedToEmails: courseData.restrictedToEmails,

    // Pricing
    // €0 is a valid base price (free courses billed directly to a college) —
    // only fall back to the default when the value is genuinely absent
    basePrice: courseData.basePrice !== null && courseData.basePrice !== undefined && courseData.basePrice !== ''
      ? parseFloat(courseData.basePrice) : 299.00,
    currency: courseData.currency || 'EUR',

    // Visibility options - preserve explicit false values
    hideFromGuardian: courseData.hideFromGuardian !== undefined ? courseData.hideFromGuardian : false,
    hideFromStudent: courseData.hideFromStudent !== undefined ? courseData.hideFromStudent : false,

    // Form association
    formId: courseData.formId,
    formFields: courseData.formSchema || courseData.formFields || [],

    // Data management fields - CRITICAL FIX
    coursePrefix: courseData.coursePrefix || null,
    courseTypeId: courseData.courseTypeId || courseData.courseType || null,
    centreId: courseData.centreId || courseData.centre || null,
    centreName: courseData.centreName || null,
    targetYearLevels: courseData.targetYearLevels || [],
    studentEligibility: courseData.studentEligibility || null,

    // Age restrictions
    minAge: courseData.minAge || null,
    maxAge: courseData.maxAge || null,

    // Gender-based slots
    genderPolicy: courseData.genderPolicy || 'mixed',
    maleSlots: courseData.maleSlots || courseData.slotsMale || 0,
    femaleSlots: courseData.femaleSlots || courseData.slotsFemale || 0,
    mixedSlots: courseData.mixedSlots || 0,
    virtualSlots: courseData.virtualSlots || 0,

    // PAYMENT TYPES: Transform frontend paymentTypes to backend format
    paymentTypes: courseData.paymentTypes ? courseData.paymentTypes.map(pt => ({
      paymentTypeId: pt.paymentTypeId,
      isDefault: pt.isDefault || false,
      isAvailable: pt.isAvailable !== false,
      customAmount: pt.customAmount !== null && pt.customAmount !== undefined && pt.customAmount !== ''
        ? parseFloat(pt.customAmount) : null,

      // Email restrictions for this specific payment type
      restrictedToEmails: pt.restrictedToEmails || null,

      // Additional payment type metadata
      requiresApproval: pt.requiresApproval || false,
      validFrom: pt.validFrom || null,
      validUntil: pt.validUntil || null,
      maxUses: pt.maxUses || null,

      // Discount/pricing overrides
      discountPercentage: pt.discountPercentage || null,
      discountAmount: pt.discountAmount || null,

      // Notes/description for this assignment
      notes: pt.notes || null,

      // Keep existing assignment ID if updating
      assignmentId: pt.assignmentId || null
    })) : [],

    // Email-specific restrictions (separate from payment types)
    emailRestrictions: courseData.emailRestrictions ? courseData.emailRestrictions.map(restriction => ({
      email: restriction.email.toLowerCase(),
      allowedPaymentTypes: restriction.allowedPaymentTypes || [],
      customPricing: restriction.customPricing || null,
      specialDiscount: restriction.specialDiscount || null,
      notes: restriction.notes || null,
      isActive: restriction.isActive !== false,

      // Keep existing restriction ID if updating
      restrictionId: restriction.restrictionId || null
    })) : [],

    // Find default payment type ID
    defaultPaymentTypeId: courseData.paymentTypes ?
      courseData.paymentTypes.find(pt => pt.isDefault)?.paymentTypeId : null,

    // Publishing settings
    publishDate: courseData.publishDate,
    visibleFrom: courseData.visibleFrom
  };

  console.log('=== UPDATE FRONTEND PAYLOAD DEBUG ===');
  console.log('🔍 Original courseData contains:', {
    coursePrefix: courseData.coursePrefix,
    courseType: courseData.courseType,
    courseTypeId: courseData.courseTypeId,
    centre: courseData.centre,
    centreId: courseData.centreId,
    centreName: courseData.centreName,
    studentEligibility: courseData.studentEligibility,
    slotsMale: courseData.slotsMale,
    slotsFemale: courseData.slotsFemale,
    maleSlots: courseData.maleSlots,
    femaleSlots: courseData.femaleSlots
  });
  console.log('📤 Final payload contains:', {
    coursePrefix: payload.coursePrefix,
    courseTypeId: payload.courseTypeId,
    centreId: payload.centreId,
    centreName: payload.centreName,
    studentEligibility: payload.studentEligibility,
    maleSlots: payload.maleSlots,
    femaleSlots: payload.femaleSlots
  });
  console.log('CourseId:', courseId);
  console.log('Original courseData.paymentMethods:', courseData.paymentMethods);
  console.log('Original courseData.paymentTypes:', courseData.paymentTypes);
  console.log('Transformed payload.paymentMethods:', payload.paymentMethods);
  console.log('Transformed payload.paymentTypes:', payload.paymentTypes);
  console.log('Complete updatePayload:', JSON.stringify(payload, null, 2));

  return await put({
    apiName: API_NAME,
    path: `admin/courses/${courseId}`,
    options: {
      body: payload,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get course details by ID (admin version with form details)
 */
export const getCourseById = (courseId) => handleApiCall(async function getCourseById() {
  return await get({
    apiName: API_NAME,
    path: `courses/${courseId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

export const getAdminCourseById = (courseId) => handleApiCall(async function getCourseById() {
  return await get({
    apiName: API_NAME,
    path: `admin/courses/${courseId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

// Get comprehensive course book data
export const getCourseBookData = async (courseId) => {
  console.log('📚 Getting course book data for course:', courseId);
  
  return handleApiCall(async () => {
    const response = await get({
      apiName: API_NAME,
      path: `admin/courses/${courseId}/course-book`,
      options: {
        headers: await getAuthHeaders()
      }
    });
    console.log('📚 Course book data response:', response);
    return response;
  });
};

/**
 * Fetch courses for admin dashboard
 */
export const fetchAdminCourses = (status = 'all', view = 'default', options = {}) => handleApiCall(async function fetchAdminCourses() {
  console.log('Calling fetchAdminCourses API with status:', status, 'view:', view, 'options:', options);
  const headers = await getAuthHeaders();

  const params = new URLSearchParams();
  if (status !== 'all') params.append('status', status);
  if (view !== 'default') params.append('view', view);
  // Support for fast dropdown loading - minimal=true skips expensive per-course queries
  if (options.minimal) params.append('minimal', 'true');
  // Support for filtering by specific statuses - e.g., "published,completed"
  if (options.statuses) params.append('statuses', options.statuses);

  const queryParams = params.toString() ? `?${params.toString()}` : '';
  console.log('❓Query Params:', queryParams);

  // Just return the get() result—no parsing/unwrapping here
  return await get({
    apiName: API_NAME,
    path: `admin/courses${queryParams}`,
    options: { headers }
  });
});



/**
 * Publish a course
 */
export const publishCourse = (courseId) => handleApiCall(async function publishCourse() {
  return await put({
    apiName: API_NAME,
    path: `admin/courses/${courseId}/publish`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Unpublish a course
 */
export const unpublishCourse = (courseId) => handleApiCall(async function unpublishCourse() {
  return await put({
    apiName: API_NAME,
    path: `admin/courses/${courseId}/unpublish`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Delete a course
 */
export const deleteCourse = (courseId) => handleApiCall(async function deleteCourse() {
  return await del({
    apiName: API_NAME,
    path: `admin/courses/${courseId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get deletion preview (counts of related data) for a course
 */
export const getCourseDeletePreview = (courseId) => handleApiCall(async function getCourseDeletePreview() {
  return await get({
    apiName: API_NAME,
    path: `admin/courses/${courseId}/deletion-preview`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Archive and delete a course (super admin only)
 */
export const archiveAndDeleteCourse = (courseId, data) => handleApiCall(async function archiveAndDeleteCourse() {
  return await post({
    apiName: API_NAME,
    path: `admin/courses/${courseId}/archive-delete`,
    options: {
      headers: await getAuthHeaders(),
      body: data
    }
  });
});

/**
 * Duplicate a course
 */
export const duplicateCourse = (courseId) => handleApiCall(async function duplicateCourse() {
  return await post({
    apiName: API_NAME,
    path: `admin/courses/${courseId}/duplicate`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get course analytics
 */
export const getCourseAnalytics = (courseId) => handleApiCall(async function getCourseAnalytics() {
  return await get({
    apiName: API_NAME,
    path: `admin/courses/${courseId}/analytics`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

// ===============================
// FORM MANAGEMENT FUNCTIONS  
// ===============================

/**
 * Create a new form
 */
export const createForm = (formData) => handleApiCall(async function createForm() {
  return await post({
    apiName: API_NAME,
    path: '/admin/forms',
    options: {
      body: formData,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Update an existing form
 */
export const updateForm = (formId, formData) => handleApiCall(async function updateForm() {
  return await put({
    apiName: API_NAME,
    path: `admin/forms/${formId}`,
    options: {
      body: formData,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get form by ID
 */
export const getFormById = (formId) =>
  handleApiCall(async function getFormById() {
    const result = await get({
      apiName: API_NAME,
      path: `admin/forms/${formId}`,
      options: { headers: await getAuthHeaders() }
    });
    // Don't parse anything here! Just return.
    return result;
  });

export const getStudentFormById = (formId) =>
  handleApiCall(async function getFormById() {
    const result = await get({
      apiName: API_NAME,
      path: `forms/${formId}`,
      options: { headers: await getAuthHeaders() }
    });
    // Don't parse anything here! Just return.
    return result;
  });




/**
 * List all forms
 */
/**
 * List all forms - Debug version
 */
export const listForms = () =>
  handleApiCall(async function listForms() {
    return await get({
      apiName: API_NAME,
      path: '/admin/forms',
      options: { headers: await getAuthHeaders() }
    });
  }).then(response => {
    // Now response is the flat object, thanks to handleApiCall
    if (response && Array.isArray(response.forms)) return response.forms;
    if (Array.isArray(response)) return response;
    return [];
  });




// NO handleApiCall abstraction. Just make it raw and readable!
export const listFormsWORKS = async () => {
  const result = await get({
    apiName: API_NAME,
    path: '/admin/forms',
    options: { headers: await getAuthHeaders() }
  });

  // Unwrap response if needed
  let response = result;
  if (response && response.response && typeof response.response.then === 'function') {
    response = await response.response;
  }
  //console.log("UNWRAPPED API RESPONSE in listForms:", response);

  let forms = [];

  // Handle ReadableStream body (the AWS Amplify REST client sometimes returns this)
  if (response && response.body && typeof response.body.getReader === 'function') {
    // Parse the ReadableStream as text, then parse as JSON!
    const reader = response.body.getReader();
    let chunks = [];
    let done, value;
    while (true) {
      ({ done, value } = await reader.read());
      if (done) break;
      if (value) chunks.push(...value);
    }
    const text = new TextDecoder().decode(new Uint8Array(chunks));
    console.log("DECODED TEXT BODY:", text);
    try {
      const parsed = JSON.parse(text);
      console.log("PARSED JSON BODY:", parsed);
      if (Array.isArray(parsed.forms)) forms = parsed.forms;
    } catch (e) {
      console.error('Body parse error:', e);
    }
  } else if (response && typeof response.body === 'string') {
    try {
      const parsed = JSON.parse(response.body);
      if (Array.isArray(parsed.forms)) forms = parsed.forms;
    } catch (e) {
      console.error('Body parse error:', e);
    }
  } else if (response && Array.isArray(response.forms)) {
    forms = response.forms;
  } else if (Array.isArray(response)) {
    forms = response;
  }

  console.log("RETURNING FORMS ARRAY:", forms);
  return forms;
};











/**
 * Delete a form
 */
export const deleteForm = (formId) => handleApiCall(async function deleteForm() {
  return await del({
    apiName: API_NAME,
    path: `admin/forms/${formId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

// ===============================
// PUBLIC COURSE FUNCTIONS
// ===============================

/**
 * Fetch public courses (no auth required)
 */
export const fetchCourses = (includeIneligible = false) => handleApiCall(async function fetchCourses() {
  console.log('🎯 ELIGIBILITY DEBUG: Calling fetchCourses API with eligibility filtering...');
  console.log('🎯 ELIGIBILITY DEBUG: includeIneligible =', includeIneligible);

  const queryParams = new URLSearchParams({
    includeIneligible: includeIneligible.toString()
  });

  const fullPath = `courses?${queryParams.toString()}`;
  console.log('🎯 ELIGIBILITY DEBUG: Full API path =', fullPath);

  const headers = await getAuthHeaders();
  console.log('🎯 ELIGIBILITY DEBUG: Request headers =', headers);

  const response = await get({
    apiName: API_NAME,
    path: fullPath,
    options: {
      headers: headers
    }
  });

  console.log('🎯 ELIGIBILITY DEBUG: Raw API response =', response);
  
  const finalResult = response.courses || response || [];
  console.log('🎯 ELIGIBILITY DEBUG: Final courses count =', Array.isArray(finalResult) ? finalResult.length : 'Not an array');
  console.log('🎯 ELIGIBILITY DEBUG: Final courses =', finalResult);

  return finalResult;
});

/**
 * Fetch courses with view filter (for admin tabs)
 */
export const fetchCoursesWithView = (view = 'default') => handleApiCall(async function fetchCoursesWithView() {
  console.log('Calling fetchCoursesWithView API with view:', view);

  const queryParams = view !== 'default' ? `?view=${view}` : '';
  const response = await get({
    apiName: API_NAME,
    path: `courses${queryParams}`,
    options: {
      headers: await getAuthHeaders()
    }
  });

  return response.courses || response || [];
});

/**
 * Get public course details
 */
export const getPublicCourse = (courseId) => handleApiCall(async function getPublicCourse() {
  return await get({
    apiName: API_NAME,
    path: `courses/${courseId}`
  });
});

// ===============================
// BOOKING FUNCTIONS
// ===============================

/**
 * Start booking process
 */
export const startBooking = (bookingData) => handleApiCall(async function startBooking() {
  return await post({
    apiName: API_NAME,
    path: `courses/${bookingData.courseId}/book`,
    options: {
      body: bookingData,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Complete booking
 */
export const completeBooking = (bookingId) => handleApiCall(async function completeBooking() {
  return await post({
    apiName: API_NAME,
    path: `bookings/${bookingId}/complete`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Cancel booking
 */
export const cancelBooking = (bookingId) => handleApiCall(async function cancelBooking() {
  return await post({
    apiName: API_NAME,
    path: `bookings/${bookingId}/cancel`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Delete incomplete booking
 */
export const deleteBooking = (bookingId) => handleApiCall(async function deleteBooking() {
  return await del({
    apiName: API_NAME,
    path: `bookings/${bookingId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get student's bookings
 */
export const getStudentBookings = () => handleApiCall(async function getStudentBookings() {
  console.log('Calling getStudentBookings API...');
  const response = await get({
    apiName: API_NAME,
    path: '/students/me/bookings',
    options: {
      headers: await getAuthHeaders()
    }
  });
  return response.bookings || response || [];
});

/**
 * Get specific booking details
 */
export const getStudentBookingById = (bookingId) => handleApiCall(async function getStudentBookingById() {
  return await get({
    apiName: API_NAME,
    path: `students/me/bookings/${bookingId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get related bookings created in the same session (for multi-booking resume)
 */
export const getRelatedBookings = (bookingId) => handleApiCall(async function getRelatedBookings() {
  return await get({
    apiName: API_NAME,
    path: `/bookings/${bookingId}/related`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Validate booking for payment resumption
 */
export const validateBookingForPayment = (bookingId) => handleApiCall(async function validateBookingForPayment() {
  const response = await post({
    apiName: API_NAME,
    path: `/bookings/${bookingId}/validate`,
    options: {
      headers: await getAuthHeaders(),
      body: {}
    }
  });
  return response;
});

/**
 * Get all bookings (admin)
 */
export const getAllBookings = () => handleApiCall(async function getAllBookings() {
  const response = await get({
    apiName: API_NAME,
    path: '/admin/bookings',
    options: {
      headers: await getAuthHeaders()
    }
  });
  return response.bookings || response || [];
});

/**
 * Get course bookings (admin)
 */
export const getCourseBookings = (courseId) => handleApiCall(async function getCourseBookings() {
  const response = await get({
    apiName: API_NAME,
    path: `admin/courses/${courseId}/bookings`,
    options: {
      headers: await getAuthHeaders()
    }
  });
  return response.bookings || response || [];
});

// ===============================
// WAITLIST FUNCTIONS
// ===============================

/**
 * Join waitlist
 */
// Fixed joinWaitlist function for AWS Amplify REST API
export const joinWaitlist = (data) => handleApiCall(async function joinWaitlist() {
  console.log('Calling joinWaitlist API with data:', data);

  try {
    // Amplify's post method returns the parsed response directly
    const response = await post({
      apiName: API_NAME,
      path: 'waitlist/join', // No leading slash - endpoint has trailing slash
      options: {
        body: data,
        headers: await getAuthHeaders()
      }
    });

    //console.log('Waitlist API response:', response);

    // If we get here, the request was successful
    // Amplify throws errors for non-2xx responses
    return response;

  } catch (error) {
    console.error('Waitlist API error:', error);

    // Amplify wraps the error response
    if (error.response) {
      // The error.response contains the parsed error data from the server
      const errorMessage = error.response?.message ||
        error.response?.data?.message ||
        error.message ||
        'Failed to join waitlist';

      // Create a properly structured error
      const structuredError = new Error(errorMessage);
      structuredError.response = {
        data: error.response,
        status: error.response?.statusCode || error.response?.status
      };
      throw structuredError;
    }

    // Network or other errors
    const networkError = new Error('Network error - please check your connection');
    networkError.response = null;
    throw networkError;
  }
});

/**
 * Get waitlist entries for a guardian or student
 */
export const getWaitlistEntries = (guardianEmail) => handleApiCall(async function getWaitlistEntries() {
  console.log('Getting waitlist entries for:', guardianEmail);

  try {
    const response = await get({
      apiName: API_NAME,
      path: guardianEmail ? `/waitlist/entries?guardianEmail=${encodeURIComponent(guardianEmail)}` : '/waitlist/entries',
      options: {
        headers: await getAuthHeaders()
      }
    });

    console.log('Waitlist entries response:', response);
    return response;

  } catch (error) {
    console.error('Failed to get waitlist entries:', error);
    
    if (error.response) {
      const errorMessage = error.response?.message ||
        error.response?.data?.message ||
        error.message ||
        'Failed to get waitlist entries';

      const structuredError = new Error(errorMessage);
      structuredError.response = {
        data: error.response,
        status: error.response?.statusCode || error.response?.status
      };
      throw structuredError;
    }

    const networkError = new Error('Failed to load waitlist entries');
    networkError.response = null;
    throw networkError;
  }
});

/**
 * Get student's waitlist entries
 */
export const getMyWaitlist = () => handleApiCall(async function getMyWaitlist() {
  console.log('🚀 Calling getMyWaitlist API...');
  const response = await get({
    apiName: API_NAME,
    path: '/students/me/waitlist',
    options: {
      headers: await getAuthHeaders()
    }
  });
  return response.waitlist || response || [];
});

/**
 * Get course waitlist (admin)
 */
export const getCourseWaitlist = (courseId) => handleApiCall(async function getCourseWaitlist() {
  const response = await get({
    apiName: API_NAME,
    path: `/admin/waitlist/${courseId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
  return response.waitlist || response || [];
});

/**
 * Book student from waitlist (admin)
 */
export const bookFromWaitlist = (courseId, studentId) => handleApiCall(async function bookFromWaitlist() {
  return await post({
    apiName: API_NAME,
    path: `/admin/waitlist/${courseId}/notify`,
    options: {
      body: { studentId },
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Remove from waitlist
 */
export const removeFromWaitlist = (courseId, studentEmail, entry = null) => handleApiCall(async function removeFromWaitlist() {
  const queryParams = { studentEmail };
  // Pass PK/SK for direct lookup (no scan needed)
  if (entry?.PK && entry?.SK) {
    queryParams.PK = entry.PK;
    queryParams.SK = entry.SK;
  }
  if (entry?.queueId) {
    queryParams.queueId = entry.queueId;
  }
  return await del({
    apiName: API_NAME,
    path: `/courses/${courseId}/waitlist/leave`,
    options: {
      headers: await getAuthHeaders(),
      queryParams
    }
  });
});

// ===============================
// PAYMENT FUNCTIONS
// ===============================

/**
 * Make payment
 */
export const makePayment = (data) => handleApiCall(async function makePayment() {
  return await post({
    apiName: API_NAME,
    path: `students/me/bookings/${data.bookingId}/pay`,
    options: {
      body: data,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get student payments
 */
export const getStudentPayments = () => handleApiCall(async function getStudentPayments() {
  console.log('Calling getStudentPayments API...');
  const response = await get({
    apiName: API_NAME,
    path: '/students/me/payments',
    options: {
      headers: await getAuthHeaders()
    }
  });
  return response.payments || response || [];
});

// ===============================
// REPORT FUNCTIONS
// ===============================

/**
 * Generate student report
 */
export const generateStudentReport = () => handleApiCall(async function generateStudentReport() {
  return await get({
    apiName: API_NAME,
    path: '/admin/reports/students',
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Generate payment report
 */
export const generatePaymentReport = () => handleApiCall(async function generatePaymentReport() {
  return await get({
    apiName: API_NAME,
    path: '/admin/reports/payments',
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Generate course report
 */
export const generateCourseReport = () => handleApiCall(async function generateCourseReport() {
  return await get({
    apiName: API_NAME,
    path: '/admin/reports/courses',
    options: {
      headers: await getAuthHeaders()
    }
  });
});

// ===============================
// UTILITY FUNCTIONS
// ===============================

/**
 * Check API health
 */
export const checkApiHealth = () => handleApiCall(async function checkApiHealth() {
  return await get({
    apiName: API_NAME,
    path: '/debug'
  });
});

/**
 * Check authenticated API health
 */
export const checkAuthHealth = () => handleApiCall(async function checkAuthHealth() {
  return await get({
    apiName: API_NAME,
    path: '/debug-auth',
    options: {
      headers: await getAuthHeaders()
    }
  });
});


/**
* Validate discount code
*/
export const validateDiscountCode = (code, courseId) =>
  handleApiCall(async function validateDiscountCode() {
    const result = await post({
      apiName: API_NAME,
      path: '/discount/validate',
      options: {
        headers: await getAuthHeaders(),
        body: {
          code,
          courseId
        }
      }
    });
    return result;
  });
/**
 * Get payment amounts for a course
 */
export const getPaymentAmount = (courseId) =>
  handleApiCall(async function getPaymentAmount() {
    const result = await get({
      apiName: API_NAME,
      path: `courses/${courseId}/payment-amounts`,
      options: { headers: await getAuthHeaders() }
    });
    return result;
  });
/**
* Process payment completion from GlobalPay webhook
*/
export const processPaymentCompletion = (paymentData) =>
  handleApiCall(async function processPaymentCompletion() {
    const result = await post({
      apiName: API_NAME,
      path: '/payment/complete',
      options: {
        headers: await getAuthHeaders(),
        body: paymentData
      }
    });
    return result;
  });

/**
 * Cancel expired booking and release slot
 */
export const cancelExpiredBooking = (bookingId) =>
  handleApiCall(async function cancelExpiredBooking() {
    const result = await put({
      apiName: API_NAME,
      path: `bookings/${bookingId}/expire`,
      options: {
        headers: await getAuthHeaders(),
        body: {
          status: 'expired'
        }
      }
    });
    return result;
  });

/**
 * Get booking receipt/invoice
 */
export const getBookingReceipt = (bookingId) =>
  handleApiCall(async function getBookingReceipt() {
    const result = await get({
      apiName: API_NAME,
      path: `bookings/${bookingId}/receipt`,
      options: { headers: await getAuthHeaders() }
    });
    return result;
  });

/**
 * Send booking confirmation email
 */
export const sendBookingConfirmation = (bookingId) =>
  handleApiCall(async function sendBookingConfirmation() {
    const result = await post({
      apiName: API_NAME,
      path: `bookings/${bookingId}/send-confirmation`,
      options: {
        headers: await getAuthHeaders(),
        body: {}
      }
    });
    return result;
  });

/**
 * Get available discount codes for a course
 */
export const getAvailableDiscounts = (courseId) =>
  handleApiCall(async function getAvailableDiscounts() {
    const result = await get({
      apiName: API_NAME,
      path: `courses/${courseId}/discounts`,
      options: { headers: await getAuthHeaders() }
    });
    return result;
  });

/**
 * Check if user is eligible for scholarship
 */
export const checkScholarshipEligibility = (courseId, userProfile) =>
  handleApiCall(async function checkScholarshipEligibility() {
    const result = await post({
      apiName: API_NAME,
      path: '/scholarship/check-eligibility',
      options: {
        headers: await getAuthHeaders(),
        body: {
          courseId,
          userProfile
        }
      }
    });
    return result;
  });


// ===============================
// ADD THESE NEW FUNCTIONS TO YOUR EXISTING courseService.js
// (Don't duplicate any existing functions)
// ===============================

/**
 * Check course availability and get real-time slot information
 * NEW FUNCTION - Add this to your courseService.js
 */
export const checkAvailability = (courseId) => handleApiCall(async function checkAvailability() {
  //console.log('🔍 [DEBUG] Checking availability for course:', courseId);
  
  const result = await get({
    apiName: API_NAME,
    path: `courses/${courseId}/availability`,
    options: {
      headers: await getAuthHeaders()
    }
  });
  
/*   //console.log('🔍 [DEBUG] checkAvailability API response for course', courseId, ':', {
    rawResponse: result,
    availableSpots: result?.availableSpots,
    totalSpots: result?.totalSpots,
    confirmedBookings: result?.confirmedBookings,
    activeReservations: result?.activeReservations,
    effectiveTotalSpots: result?.effectiveTotalSpots,
    isAvailable: result?.isAvailable
  });
   */
  return result;
});

/**
 * Check if current student is eligible for a course
 */
export const checkCourseEligibility = (courseId) => handleApiCall(async function checkCourseEligibility() {
  //console.log('🔍 [DEBUG] Checking eligibility for course:', courseId);
  
  const result = await get({
    apiName: API_NAME,
    path: `courses/${courseId}/eligibility`,
    options: {
      headers: await getAuthHeaders()
    }
  });
  
  //console.log('🔍 [DEBUG] checkCourseEligibility API response for course', courseId, ':', result);
  
  return result;
});

/**
 * Release a temporarily reserved booking slot
 * NEW FUNCTION - Add this to your courseService.js
 */
export const releaseBookingSlot = (bookingId) => handleApiCall(async function releaseBookingSlot() {
  console.log('Releasing booking slot:', bookingId);
  return await post({
    apiName: API_NAME,
    path: `bookings/${bookingId}/release`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Process payment with Global Pay integration
 * NEW FUNCTION - Add this to your courseService.js
 */
export const processPayment = (paymentData) => handleApiCall(async function processPayment() {
  console.log('Processing payment:', paymentData);
  return await post({
    apiName: API_NAME,
    path: '/payments/process',
    options: {
      body: paymentData,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get payment status by transaction ID
 * NEW FUNCTION - Add this to your courseService.js
 */
export const getPaymentStatus = (transactionId) => handleApiCall(async function getPaymentStatus() {
  return await get({
    apiName: API_NAME,
    path: `payments/${transactionId}/status`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get waitlist position for user and course
 * NEW FUNCTION - Add this to your courseService.js
 */
export const getWaitlistPosition = (courseId, userId) => handleApiCall(async function getWaitlistPosition() {
  return await get({
    apiName: API_NAME,
    path: `/courses/${courseId}/waitlist/${userId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Process waitlist when spots become available
 * NEW FUNCTION - Add this to your courseService.js
 */
export const processWaitlist = (courseId) => handleApiCall(async function processWaitlist() {
  return await post({
    apiName: API_NAME,
    path: `/courses/${courseId}/waitlist/process`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Send waitlist notification
 * NEW FUNCTION - Add this to your courseService.js
 */
export const sendWaitlistNotification = (courseId, userId) => handleApiCall(async function sendWaitlistNotification() {
  return await post({
    apiName: API_NAME,
    path: `/courses/${courseId}/waitlist/${userId}/notify`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Check if user has a valid promotion for a course
 */
export const checkPromotionStatus = (courseId) => handleApiCall(async function checkPromotionStatus() {
  const response = await get({
    apiName: API_NAME,
    path: `/courses/${courseId}/waitlist/promotion-status`,
    options: {
      headers: await getAuthHeaders()
    }
  });
  return response;
});

/**
 * Upload file for form fields
 * NEW FUNCTION - Add this to your courseService.js
 */
export const uploadFile = async (file, context = 'booking') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('context', context);

  const session = await fetchAuthSession();

  const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000/api'}/upload`, {
    method: 'POST',
    headers: {
      ...(session?.tokens?.idToken && {
        Authorization: `Bearer ${session.tokens.idToken.toString()}`
      }),
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error('File upload failed');
  }

  return response.json();
};

/**
 * Submit form data for booking
 * NEW FUNCTION - Add this to your courseService.js
 */
export const submitBookingForm = (bookingId, formData) => handleApiCall(async function submitBookingForm() {
  return await post({
    apiName: API_NAME,
    path: `bookings/${bookingId}/form`,
    options: {
      body: formData,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Save form progress for booking
 * NEW FUNCTION - Add this to your courseService.js
 */
export const saveFormProgress = (bookingId, progressData) => handleApiCall(async function saveFormProgress() {
  console.log('Saving form progress for booking:', bookingId, 'with data:', progressData);
  return await put({
    apiName: API_NAME,
    path: `bookings/${bookingId}/form-progress`,
    options: {
      body: {
        formProgress: progressData,
        formStatus: 'in_progress',
        lastSaved: new Date().toISOString()
      },
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Generate calendar event for booking
 * NEW FUNCTION - Add this to your courseService.js
 */
export const generateCalendarEvent = (bookingId) => handleApiCall(async function generateCalendarEvent() {
  return await get({
    apiName: API_NAME,
    path: `bookings/${bookingId}/calendar-event`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get course schedule
 * NEW FUNCTION - Add this to your courseService.js
 */
export const getCourseSchedule = (courseId) => handleApiCall(async function getCourseSchedule() {
  return await get({
    apiName: API_NAME,
    path: `courses/${courseId}/schedule`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

// ===============================
// UPDATE YOUR EXISTING FUNCTIONS
// ===============================

/**
 * MODIFY your existing createBooking function to include timer
 * REPLACE the existing createBooking function with this:
 */
export const createBooking = (bookingData) => handleApiCall(async function createBooking() {
  console.log('Creating booking with data:', bookingData);
  return await post({
    apiName: API_NAME,
    path: '/bookings',
    options: {
      body: {
        ...bookingData,
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString() // 5 minutes
      },
      headers: await getAuthHeaders()
    }
  });
});

/**
 * MODIFY your existing updateBookingStatus function
 * REPLACE the existing updateBookingStatus function with this:
 */
export const updateBookingStatus = (bookingId, updateData) => handleApiCall(async function updateBookingStatus() {
  console.log('Updating booking:', bookingId, 'with data:', updateData);
  return await put({
    apiName: API_NAME,
    path: `bookings/${bookingId}`,
    options: {
      body: {
        ...updateData,
        updatedAt: new Date().toISOString()
      },
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Update booking form data
 */
export const updateBookingForm = (bookingId, formData) => handleApiCall(async function updateBookingForm() {
  console.log('Updating booking form:', bookingId);
  return await put({
    apiName: API_NAME,
    path: `bookings/${bookingId}/form`,
    options: {
      body: {
        formData: formData,
        updatedAt: new Date().toISOString()
      },
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Create payment session - NOW CREATES ACCESS TOKEN
 */
export const createPaymentSession = (paymentData) =>
  handleApiCall(async function createPaymentSession() {
    console.log('Creating Global Pay access token:', paymentData);
    const authHeaders = await getAuthHeaders();
    return await post({
      apiName: API_NAME,
      path: '/payment/access-token',
      options: {
        headers: {
          'X-Origin': window.location.origin,
          'X-Referer': window.location.href,
          ...authHeaders
        },
        body: {
          // Send both bookingId and bookingIds for compatibility
          bookingId: paymentData.bookingId,  // Single booking ID
          bookingIds: paymentData.relatedBookings || [paymentData.bookingId], // Array of booking IDs
          amount: paymentData.amount,
          currency: paymentData.currency || 'EUR',
          // ADD THESE FIELDS:
          email: paymentData.email,
          customerName: paymentData.customerName,
          phone: paymentData.phone,
          billingAddress1: paymentData.billingAddress1,
          billingAddress2: paymentData.billingAddress2,
          billingCity: paymentData.billingCity,
          billingPostcode: paymentData.billingPostcode,
          billingCountry: paymentData.billingCountry,
          paymentType: paymentData.paymentType || 'deposit', // CRITICAL: Include payment type
          // CRITICAL: Include multi-booking information
          isMultiBooking: paymentData.isMultiBooking,
          // Resurrection of expired/abandoned bookings is admin-only (admin form uses
          // /admin/payment/resurrect-and-pay, which re-checks capacity). Customer sessions
          // must NOT resurrect: the backend path skips the capacity check, so a stale
          // wizard tab paying after the 45-min expiry could oversell a full course.
          resurrectExpired: paymentData.resurrectExpired === true
        }
      }
    });
  });

// Replace the incorrectly formatted functions with these:

/**
 * Get available payment options for a course
 */
export const getCoursePaymentOptions = (courseId) => handleApiCall(async function getCoursePaymentOptions() {
  console.log('Fetching payment options for course:', courseId);
  return await get({
    apiName: API_NAME,
    path: `courses/${courseId}/payment-options`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Complete payment after tokenization
 */
export const completePayment = (paymentData) => handleApiCall(async function completePayment() {
  console.log('Completing payment:', paymentData);
  return await post({
    apiName: API_NAME,
    path: '/payment/complete',
    options: {
      body: paymentData,
      headers: await getAuthHeaders()
    }
  });
});

// Check if payment was completed via webhook
export const checkPaymentWebhookStatus = (sessionId) => handleApiCall(async function checkPaymentWebhookStatus() {
  console.log('Checking webhook payment status for session:', sessionId);
  return await get({
    apiName: API_NAME,
    path: `/payment/session/${sessionId}/status`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

// In courseService.js add:
export const complete3DSChallenge = (challengeData) => handleApiCall(async function complete3DSChallenge() {
  return await post({
    apiName: API_NAME,
    path: '/payment/3ds-result',
    options: {
      body: challengeData,
      headers: await getAuthHeaders()
    }
  });
});


// Add these new methods to courseService.js

/**
 * Create payment session specifically for balance payments
 * This is separate from the main payment flow to not interfere with Global Pay
 */
export const createBalancePaymentSession = (paymentData) =>
  handleApiCall(async function createBalancePaymentSession() {
    console.log('Creating balance payment session:', paymentData);
    return await post({
      apiName: API_NAME,
      path: '/payment/balance-access-token', // Use the balance-specific endpoint
      options: {
        body: {
          bookingId: paymentData.bookingId,
          bookingIds: paymentData.bookingIds, // Support for multi-student payments
          amount: paymentData.amount,
          currency: paymentData.currency || 'EUR',
          paymentType: paymentData.paymentType || 'balance', // THIS IS THE KEY ADDITION
          // Customer info for Global Pay
          email: paymentData.email,
          customerName: paymentData.customerName,
          phone: paymentData.phone,
          billingAddress1: paymentData.billingAddress1,
          billingAddress2: paymentData.billingAddress2,
          billingCity: paymentData.billingCity,
          billingPostcode: paymentData.billingPostcode,
          billingCountry: paymentData.billingCountry
        },
        headers: await getAuthHeaders()
      }
    });
  });

/**
 * Complete balance payment after Global Pay processing
 */
export const completeBalancePayment = (paymentData) =>
  handleApiCall(async function completeBalancePayment() {
    console.log('Completing balance payment:', paymentData);
    return await post({
      apiName: API_NAME,
      path: '/payment/complete-balance',
      options: {
        body: paymentData,
        headers: await getAuthHeaders()
      }
    });
  });

/**
 * Check balance payment status for a booking
 */
export const getBalancePaymentStatus = (bookingId) =>
  handleApiCall(async function getBalancePaymentStatus() {
    console.log('Getting balance payment status for booking:', bookingId);
    return await get({
      apiName: API_NAME,
      path: `bookings/${bookingId}/balance-status`,
      options: {
        headers: await getAuthHeaders()
      }
    });
  });

/**
* Update booking status (admin only)
* TODO: Implement backend handler
*/
export const updateBookingStatusAdmin = (bookingId, updateData) => handleApiCall(async function updateBookingStatusAdmin() {
  // For now, return mock success until backend is implemented
  if (process.env.NODE_ENV === 'development') {
    console.log('Mock: Updating booking status', bookingId, updateData);
    return { success: true, bookingId, ...updateData };
  }

  return await put({
    apiName: API_NAME,
    path: `admin/bookings/${bookingId}`,
    options: {
      body: updateData,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get booking details (admin only)
 * TODO: Implement backend handler
 */
export const getBookingDetailsAdmin = (bookingId) => handleApiCall(async function getBookingDetailsAdmin() {
  return await get({
    apiName: API_NAME,
    path: `admin/bookings/${bookingId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Cancel booking (admin only)
 * TODO: Implement backend handler
 */
export const cancelBookingAdmin = (bookingId, reason) => handleApiCall(async function cancelBookingAdmin() {
  // For now, return mock success until backend is implemented
  if (process.env.NODE_ENV === 'development') {
    console.log('Mock: Cancelling booking', bookingId, reason);
    return { success: true, bookingId, status: 'cancelled' };
  }

  return await put({
    apiName: API_NAME,
    path: `admin/bookings/${bookingId}`,
    options: {
      body: { status: 'cancelled', cancellationReason: reason },
      headers: await getAuthHeaders()
    }
  });
});

// Add these functions to your courseService.js file

// ===============================
// ADMIN STUDENT MANAGEMENT FUNCTIONS
// ===============================

/**
 * Get all students with search and filtering (admin only)
 */
export const getAllStudentsAdmin = (filters = {}) => handleApiCall(async function getAllStudentsAdmin() {
  console.log('Getting all students for admin with filters:', filters);
  
  // Build query parameters
  const queryParams = new URLSearchParams();
  
  if (filters.search) queryParams.append('search', filters.search);
  if (filters.searchType) queryParams.append('searchType', filters.searchType); // CRITICAL: Add searchType parameter
  if (filters.hasBookings) queryParams.append('hasBookings', filters.hasBookings);
  if (filters.registeredAfter) queryParams.append('registeredAfter', filters.registeredAfter);
  if (filters.registeredBefore) queryParams.append('registeredBefore', filters.registeredBefore);
  if (filters.limit) queryParams.append('limit', filters.limit);
  if (filters.lastKey) queryParams.append('lastKey', filters.lastKey);
  
  const queryString = queryParams.toString();
  const path = `admin/students${queryString ? `?${queryString}` : ''}`;
  
  return await get({
    apiName: API_NAME,
    path: path,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get detailed student information (admin only)
 */
export const getStudentDetailsAdmin = (studentId) => handleApiCall(async function getStudentDetailsAdmin() {
  console.log('Getting student details for admin:', studentId);
  
  // URL encode the studentId to handle special characters in emails
  const encodedStudentId = encodeURIComponent(studentId);
  
  return await get({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get student's bookings from admin perspective
 */
export const getStudentBookingsAdmin = (studentId) => handleApiCall(async function getStudentBookingsAdmin() {
  console.log('Getting student bookings for admin:', studentId);
  
  // URL encode the studentId to handle special characters in emails
  const encodedStudentId = encodeURIComponent(studentId);
  
  return await get({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}/bookings`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get student's payment history (admin only)
 */
export const getStudentPaymentsAdmin = (studentId) => handleApiCall(async function getStudentPaymentsAdmin() {
  console.log('Getting student payments for admin:', studentId);

  // URL encode the studentId to handle special characters in emails
  const encodedStudentId = encodeURIComponent(studentId);

  return await get({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}/payments`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Search students by various criteria
 */
export const searchStudents = (searchTerm, filters = {}) => handleApiCall(async function searchStudents() {
  console.log('Searching students:', searchTerm, filters);
  
  return await getAllStudentsAdmin({
    search: searchTerm,
    ...filters
  });
});

/**
 * Get student statistics for admin dashboard
 */
export const getStudentStats = () => handleApiCall(async function getStudentStats() {
  console.log('Getting student statistics - using placeholder data');
  
  // TODO: This should be a dedicated stats endpoint on the backend
  // For now, return placeholder stats to avoid fetching all students
  // This prevents the duplicate API call with limit: 1000
  
  return {
    totalStudents: '-',  // Will be calculated from paginated results
    studentsWithBookings: '-',
    totalRevenue: '-',
    averageSpent: '-',
    newStudentsThisMonth: '-'
  };
  
  /* Original code - disabled to prevent fetching all students
  const studentsData = await getAllStudentsAdmin({ limit: 1000 });
  
  if (!studentsData || !studentsData.students) {
    return {
      totalStudents: 0,
      studentsWithBookings: 0,
      totalRevenue: 0,
      averageSpent: 0,
      newStudentsThisMonth: 0
    };
  }
  
  const students = studentsData.students;
  const now = new Date();
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  
  return {
    totalStudents: students.length,
    studentsWithBookings: students.filter(s => s.bookingCount > 0).length,
    totalRevenue: students.reduce((sum, s) => sum + (s.totalSpent || 0), 0),
    averageSpent: students.length > 0 ? 
      students.reduce((sum, s) => sum + (s.totalSpent || 0), 0) / students.length : 0,
    newStudentsThisMonth: students.filter(s => 
      new Date(s.registeredDate) >= thisMonth
    ).length
  };
  */
});

// Add these functions to your courseService.js file

// ===============================
// ADMIN EMAIL & COMMUNICATION FUNCTIONS
// ===============================

/**
 * Send welcome email to student
 */
export const sendWelcomeEmail = (studentId, customMessage = null) => handleApiCall(async function sendWelcomeEmail() {
  console.log('Sending welcome email to student:', studentId);
  const encodedStudentId = encodeURIComponent(studentId);

  return await post({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}/send-welcome`,
    options: {
      body: {
        emailType: 'welcome',
        customMessage: customMessage
      },
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Send payment reminder to student
 */
export const sendPaymentReminder = (studentId, bookingId = null, dueDate = null) => handleApiCall(async function sendPaymentReminder() {
  console.log('Sending payment reminder to student:', studentId, 'booking:', bookingId);
  const encodedStudentId = encodeURIComponent(studentId);

  return await post({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}/send-payment-reminder`,
    options: {
      body: {
        emailType: 'payment_reminder',
        bookingId: bookingId,
        dueDate: dueDate
      },
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Send form completion reminder to student
 */
export const sendFormReminder = (studentId, bookingId = null) => handleApiCall(async function sendFormReminder() {
  console.log('Sending form reminder to student:', studentId);
  const encodedStudentId = encodeURIComponent(studentId);

  return await post({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}/send-form-reminder`,
    options: {
      body: {
        emailType: 'form_reminder',
        bookingId: bookingId
      },
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Send custom email to student
 */
export const sendCustomEmail = (studentId, subject, message, emailType = 'custom') => handleApiCall(async function sendCustomEmail() {
  console.log('Sending custom email to student:', studentId);
  const encodedStudentId = encodeURIComponent(studentId);

  return await post({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}/send-email`,
    options: {
      body: {
        emailType: emailType,
        subject: subject,
        message: message
      },
      headers: await getAuthHeaders()
    }
  });
});

// ===============================
// ADMIN STUDENT NOTES FUNCTIONS
// ===============================

/**
 * Add admin note to student
 */
export const addStudentNote = (studentId, noteContent) => handleApiCall(async function addStudentNote() {
  console.log('Adding note to student:', studentId);
  const encodedStudentId = encodeURIComponent(studentId);

  return await post({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}/notes`,
    options: {
      body: {
        content: noteContent
      },
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get student notes
 */
export const getStudentNotes = (studentId) => handleApiCall(async function getStudentNotes() {
  console.log('Getting notes for student:', studentId);

  // URL encode the studentId to handle special characters in emails
  const encodedStudentId = encodeURIComponent(studentId);

  return await get({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}/notes`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Update student note
 */
export const updateStudentNote = (studentId, noteId, content) => handleApiCall(async function updateStudentNote() {
  console.log('Updating note for student:', studentId);
  const encodedStudentId = encodeURIComponent(studentId);

  return await put({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}/notes/${noteId}`,
    options: {
      body: {
        content: content
      },
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Delete student note
 */
export const deleteStudentNote = (studentId, noteId) => handleApiCall(async function deleteStudentNote() {
  console.log('Deleting note for student:', studentId);
  const encodedStudentId = encodeURIComponent(studentId);

  return await del({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}/notes/${noteId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

// ===============================
// ADMIN STUDENT PROFILE FUNCTIONS
// ===============================

/**
 * Update student profile (admin)
 */
export const updateStudentProfileAdmin = (studentId, profileData) => handleApiCall(async function updateStudentProfileAdmin() {
  console.log('Updating student profile (admin):', studentId, profileData);

  // URL encode the studentId to handle special characters in emails (e.g. @ symbol)
  const encodedStudentId = encodeURIComponent(studentId);

  return await put({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}/profile`,
    options: {
      body: profileData,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get student's form responses (admin view)
 */
export const getStudentFormResponses = (studentId, bookingId = null) => handleApiCall(async function getStudentFormResponses() {
  console.log('Getting form responses for student:', studentId);
  const encodedStudentId = encodeURIComponent(studentId);

  const path = bookingId
    ? `admin/students/${encodedStudentId}/form-responses/${bookingId}`
    : `admin/students/${encodedStudentId}/form-responses`;
  
  return await get({
    apiName: API_NAME,
    path: path,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get student's house assignment history
 */
export const getStudentHouseHistory = (studentId) => handleApiCall(async function getStudentHouseHistory() {
  console.log('Getting house history for student:', studentId);
  const encodedStudentId = encodeURIComponent(studentId);

  return await get({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}/house-history`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Update student's house assignment
 */
export const updateStudentHouseAssignment = (studentId, houseId, courseId) => handleApiCall(async function updateStudentHouseAssignment() {
  console.log('Updating house assignment for student:', studentId);
  const encodedStudentId = encodeURIComponent(studentId);

  return await put({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}/house-assignment`,
    options: {
      body: {
        houseId: houseId,
        courseId: courseId
      },
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Export student data
 */
export const exportStudentData = (studentId, format = 'json') => handleApiCall(async function exportStudentData() {
  console.log('Exporting student data:', studentId, format);
  const encodedStudentId = encodeURIComponent(studentId);

  return await get({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}/export?format=${format}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get student activity log
 */
export const getStudentActivityLog = (studentId, limit = 50) => handleApiCall(async function getStudentActivityLog() {
  console.log('Getting activity log for student:', studentId);
  const encodedStudentId = encodeURIComponent(studentId);

  return await get({
    apiName: API_NAME,
    path: `admin/students/${encodedStudentId}/activity?limit=${limit}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});


/**
 * Send welcome email to student (using emailService)
 * Add this to courseService.js
 */
export const sendWelcomeEmailToStudent = async (studentId, courseData = {}) => {
  try {
    // Use emailService function
    return await emailService.sendWelcomeEmailToStudent(studentId, courseData);
  } catch (error) {
    console.error('Error sending welcome email:', error);
    throw error;
  }
};

/**
 * Send payment reminder to student (using emailService)
 * Add this to courseService.js
 */
export const sendPaymentReminderToStudent = async (studentId, bookingId = null, paymentData = {}) => {
  try {
    return await emailService.sendPaymentReminderToStudent(studentId, bookingId, paymentData);
  } catch (error) {
    console.error('Error sending payment reminder:', error);
    throw error;
  }
};

/**
 * Send form reminder to student (using emailService)
 * Add this to courseService.js
 */
export const sendFormReminderToStudent = async (studentId, bookingId, formData = {}) => {
  try {
    return await emailService.sendFormReminderToStudent(studentId, bookingId, formData);
  } catch (error) {
    console.error('Error sending form reminder:', error);
    throw error;
  }
};


// Export all functions for easy importing
const courseService = {
  // Course management
  createCourse,
  updateCourse,
  getCourseById,
  getAdminCourseById,
  fetchAdminCourses,
  fetchCourses,
  publishCourse,
  unpublishCourse,
  deleteCourse,
  duplicateCourse,
  getCourseAnalytics,

  // Forms
  createForm,
  updateForm,
  getFormById,
  listForms,
  deleteForm,

  // Public courses
  getPublicCourse,

  // Bookings
  startBooking,
  completeBooking,
  cancelBooking,
  getStudentBookings,
  getStudentBookingById,
  getAllBookings,
  getCourseBookings,

  // Waitlist
  joinWaitlist,
  getMyWaitlist,
  getCourseWaitlist,
  bookFromWaitlist,
  removeFromWaitlist,
  checkPromotionStatus,

  // Payments
  makePayment,
  getStudentPayments,

  // Reports
  generateStudentReport,
  generatePaymentReport,
  generateCourseReport,

  // Utilities
  checkApiHealth,
  checkAuthHealth,
  uploadFile,

  // Booking management
  checkScholarshipEligibility,
  createBooking,
  updateBookingStatus,
  validateDiscountCode,
  getPaymentAmount,
  createPaymentSession,
  processPaymentCompletion,
  cancelExpiredBooking,
  getBookingReceipt,
  sendBookingConfirmation,
  getAvailableDiscounts,
  // Process payment completion from GlobalPay webhook
  checkAvailability,
  releaseBookingSlot,
  processPayment,
  getPaymentStatus,
  getWaitlistPosition,
  processWaitlist,
  sendWaitlistNotification,
  submitBookingForm,
  saveFormProgress,
  generateCalendarEvent,
  getCourseSchedule,

  // Payment related functions
  getCoursePaymentOptions,
  completePayment,
  complete3DSChallenge,
  createBalancePaymentSession,
  completeBalancePayment,
  getBalancePaymentStatus,

  getStudentFormById,
  getAllStudentsAdmin,
  getStudentDetailsAdmin,
  getStudentBookingsAdmin,
  getStudentPaymentsAdmin,
  searchStudents,
  getStudentStats,

  sendWelcomeEmail,
  sendPaymentReminder,
  sendFormReminder,
  sendCustomEmail,
  addStudentNote,
  getStudentNotes,
  updateStudentNote,
  deleteStudentNote,
  updateStudentProfileAdmin,
  getStudentFormResponses,
  getStudentHouseHistory,
  updateStudentHouseAssignment,
  exportStudentData,
  getStudentActivityLog,
};

export default courseService;

/**
 * Check real-time slot availability for a course
 */
export const checkSlotAvailability = async (courseId) => {
  return handleApiCall(async () => {
    const headers = await getAuthHeaders();
    const response = await get({
      apiName: API_NAME,
      path: `courses/${courseId}/availability`,
      options: { headers }
    });
    return response.response;
  });
};

// Cache management for slot availability
const slotAvailabilityCache = new Map();

/**
 * Get slot availability with caching
 */
export const getCachedSlotAvailability = async (courseId) => {
  const cacheKey = `slots_${courseId}`;
  const cached = slotAvailabilityCache.get(cacheKey);
  const CACHE_DURATION = 30000; // 30 seconds
  
  if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
    return cached.data;
  }
  
  const data = await checkSlotAvailability(courseId);
  slotAvailabilityCache.set(cacheKey, { data, timestamp: Date.now() });
  
  return data;
};

/**
 * Invalidate slot cache for a course (call after booking/cancellation)
 */
export const invalidateSlotCache = (courseId) => {
  const cacheKey = `slots_${courseId}`;
  slotAvailabilityCache.delete(cacheKey);
  console.log('Invalidated slot cache for course:', courseId);
  
  // Broadcast cache invalidation to other tabs
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem('slot_cache_invalidated', JSON.stringify({
      courseId,
      timestamp: Date.now()
    }));
    // Remove immediately to trigger storage event
    window.localStorage.removeItem('slot_cache_invalidated');
  }
};

/**
 * Listen for slot cache invalidations from other tabs
 */
export const listenForSlotCacheInvalidation = (callback) => {
  if (typeof window === 'undefined') return () => {};
  
  const handleStorageChange = (event) => {
    if (event.key === 'slot_cache_invalidated' && event.newValue) {
      try {
        const data = JSON.parse(event.newValue);
        invalidateSlotCache(data.courseId);
        callback(data.courseId);
      } catch (error) {
        console.warn('Failed to parse slot cache invalidation:', error);
      }
    }
  };
  
  window.addEventListener('storage', handleStorageChange);
  return () => window.removeEventListener('storage', handleStorageChange);
};

/**
 * Get course status for current student (booking/waitlist/promotion status)
 */
export const getStudentCourseStatus = async (courseId) => {
  return handleApiCall(async () => {
    const headers = await getAuthHeaders();
    const response = await get({
      apiName: API_NAME,
      path: `students/me/courses/${courseId}/status`,
      options: { headers }
    });
    return response.response;
  });
};
// ===========================================
// COURSE DETAILS MANAGEMENT APIS
// ===========================================

/**
 * Get course details including location, venue, schedule
 */
export const getCourseDetails = async (courseId, studentEmail = null, bookingId = null) => {
  return handleApiCall(async () => {
    const headers = await getAuthHeaders();
    // Use student endpoint that verifies booking ownership
    const queryParams = [];
    if (studentEmail) queryParams.push(`studentEmail=${encodeURIComponent(studentEmail)}`);
    if (bookingId) queryParams.push(`bookingId=${encodeURIComponent(bookingId)}`);
    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    
    const response = await get({
      apiName: API_NAME,
      path: `courses/${courseId}/details${queryString}`,  // No /admin prefix, no double slash
      options: { headers }
    });
    return response;
  });
};

/**
 * Update course details (location, venue, schedule, facilities)
 */
export const updateCourseDetails = async (courseId, details) => {
  return handleApiCall(async () => {
    const headers = await getAuthHeaders();
    const response = await put({
      apiName: API_NAME,
      path: `/admin/courses/${courseId}/details`,
      options: {
        headers,
        body: details
      }
    });
    return response;
  });
};

/**
 * Get available teachers for assignment
 */
export const getAvailableTeachers = async () => {
  return handleApiCall(async () => {
    const headers = await getAuthHeaders();
    const response = await get({
      apiName: API_NAME,
      path: `/admin/details-teachers/available`,
      options: { headers }
    });
    return response;
  });
};

/**
 * Get teachers assigned to a course
 */
export const getCourseTeachers = async (courseId) => {
  return handleApiCall(async () => {
    const headers = await getAuthHeaders();
    const response = await get({
      apiName: API_NAME,
      path: `/admin/courses/${courseId}/details-teachers`,
      options: { headers }
    });
    return response;
  });
};

/**
 * Assign teachers to a course
 */
export const assignTeachersToCourse = async (courseId, teachers) => {
  return handleApiCall(async () => {
    const headers = await getAuthHeaders();
    const response = await post({
      apiName: API_NAME,
      path: `/admin/courses/${courseId}/details-teachers`,
      options: {
        headers,
        body: { teachers }
      }
    });
    return response;
  });
};

/**
 * Remove a teacher from a course
 */
export const removeTeacherFromCourse = async (courseId, teacherEmail) => {
  return handleApiCall(async () => {
    const headers = await getAuthHeaders();
    const response = await del({
      apiName: API_NAME,
      path: `/admin/courses/${courseId}/details-teachers/${teacherEmail}`,
      options: { headers }
    });
    return response;
  });
};
